import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { Button } from '@/ui';
import { resetPassword } from './api';
import { AuthShell } from './AuthShell';
import { NewPasswordFields, validateNewPassword } from './PasswordFields';
import type { PasswordPair } from './PasswordFields';
import styles from './AuthForm.module.css';

/**
 * Handles `/reset-password?token=...`.
 *
 * That exact path is hardcoded into the emailed link by the backend, so it must not
 * change. The reset token is a separate JWT valid for 30 minutes.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [pair, setPair] = useState<PasswordPair>({ next: '', confirm: '' });

  const mutation = useMutation({
    mutationFn: () => resetPassword({ token: token ?? '', newPassword: pair.next }),
  });

  if (token === null || token.trim() === '') {
    return (
      <AuthShell
        title="Link not valid"
        subtitle="This reset link is missing its token. Request a new one."
        footer={
          <Link className={styles.link} to="/forgot-password">
            Request a new link
          </Link>
        }
      >
        <p className={styles.formError} role="alert">
          Open the link exactly as it appears in your email, or request a new one.
        </p>
      </AuthShell>
    );
  }

  if (mutation.isSuccess) {
    return (
      <AuthShell title="Password updated" subtitle="You can now sign in with your new password.">
        <div className={styles.form}>
          <p className={styles.success} role="status">
            Your password has been changed.
          </p>
          <Link to="/login">
            <Button fullWidth size="lg">
              Go to sign in
            </Button>
          </Link>
        </div>
      </AuthShell>
    );
  }

  const validation = validateNewPassword(pair);

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!validation.ok) return;
    mutation.mutate();
  };

  // A 400 here means the token is bad or expired, which is worth saying plainly
  // rather than repeating the server's terser wording.
  const isTokenProblem = mutation.error instanceof ApiError && mutation.error.status === 400;

  return (
    <AuthShell
      title="Choose a new password"
      footer={
        <Link className={styles.link} to="/login">
          Back to sign in
        </Link>
      }
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {mutation.isError && (
          <p className={styles.formError} role="alert">
            {isTokenProblem
              ? 'This reset link has expired or has already been used. Request a new one.'
              : errorMessage(mutation.error)}
          </p>
        )}

        <NewPasswordFields
          value={pair}
          onChange={setPair}
          disabled={mutation.isPending}
          serverError={
            mutation.error instanceof ApiError
              ? mutation.error.fieldError('new_password')
              : undefined
          }
        />

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={mutation.isPending}
          disabled={!validation.ok}
        >
          Update password
        </Button>
      </form>
    </AuthShell>
  );
}
